import type { FieldDefinition } from '@gzl10/nexus-sdk'

/**
 * Get expected column type for a field definition.
 * Maps Nexus field types to database column types.
 */
export function getExpectedColumnType(field: FieldDefinition, fieldName?: string): string {
  const dbConfig = field.db

  if (!dbConfig) {
    throw new Error(`Field ${fieldName ?? 'unknown'} has no db configuration`)
  }

  const type = dbConfig.type

  switch (type) {
    case 'string':
      return 'VARCHAR'
    case 'text':
      return 'TEXT'
    case 'integer':
      return 'INTEGER'
    case 'decimal':
      return 'DECIMAL'
    case 'boolean':
      return 'BOOLEAN'
    case 'datetime':
      return 'TIMESTAMP'
    case 'date':
      return 'DATE'
    case 'json':
      return 'JSON'
    case 'uuid':
      return 'UUID'
    case 'array':
      return 'JSON' // Arrays are stored as JSON
    default:
      // This should never happen if DbType is properly typed, but handle it for safety
      return String(type).toUpperCase()
  }
}

/**
 * Generate Knex column definition code from field.
 */
export function generateColumnDefinition(fieldName: string, field: FieldDefinition): string {
  const dbConfig = field.db
  if (!dbConfig) {
    throw new Error(`Field ${fieldName} has no db configuration`)
  }

  const parts: string[] = []
  const type = dbConfig.type

  // Column type and size
  switch (type) {
    case 'string':
      if (dbConfig.size) {
        parts.push(`table.string('${fieldName}', ${dbConfig.size})`)
      } else {
        parts.push(`table.string('${fieldName}')`)
      }
      break

    case 'text':
      parts.push(`table.text('${fieldName}')`)
      break

    case 'integer':
      parts.push(`table.integer('${fieldName}')`)
      break

    case 'decimal':
      if (dbConfig.precision) {
        parts.push(`table.decimal('${fieldName}', ${dbConfig.precision[0]}, ${dbConfig.precision[1]})`)
      } else {
        parts.push(`table.decimal('${fieldName}')`)
      }
      break

    case 'boolean':
      parts.push(`table.boolean('${fieldName}')`)
      break

    case 'datetime':
      parts.push(`table.timestamp('${fieldName}')`)
      break

    case 'date':
      parts.push(`table.date('${fieldName}')`)
      break

    case 'json':
      parts.push(`table.json('${fieldName}')`)
      break

    case 'array':
      parts.push(`table.json('${fieldName}')`)
      break

    case 'uuid':
      parts.push(`table.uuid('${fieldName}')`)
      break

    default:
      parts.push(`table.specificType('${fieldName}', '${type}')`)
  }

  // Nullable/NotNullable
  if (dbConfig.nullable === false) {
    parts.push('.notNullable()')
  } else if (dbConfig.nullable === true) {
    parts.push('.nullable()')
  }

  // Default function takes priority over static default
  if (dbConfig.defaultFn) {
    if (dbConfig.defaultFn === 'now') {
      parts.push('.defaultTo(knex.fn.now())')
    } else if (dbConfig.defaultFn === 'uuid') {
      parts.push('.defaultTo(knex.raw("gen_random_uuid()"))')
    }
  } else if (dbConfig.default !== undefined) {
    // Static default value (only if no defaultFn)
    if (typeof dbConfig.default === 'string') {
      if (dbConfig.default === 'CURRENT_TIMESTAMP' || dbConfig.default === 'now()') {
        parts.push('.defaultTo(knex.fn.now())')
      } else {
        parts.push(`.defaultTo('${dbConfig.default}')`)
      }
    } else if (typeof dbConfig.default === 'boolean') {
      parts.push(`.defaultTo(${dbConfig.default})`)
    } else {
      parts.push(`.defaultTo(${dbConfig.default})`)
    }
  }

  // Primary key
  if (dbConfig.primary) {
    parts.push('.primary()')
  }

  // Unique
  if (dbConfig.unique) {
    parts.push('.unique()')
  }

  // Foreign key (from relation config)
  if (field.relation?.table && field.relation?.column) {
    parts.push(`.references('${field.relation.table}.${field.relation.column}')`)

    if (field.relation.onDelete) {
      parts.push(`.onDelete('${field.relation.onDelete}')`)
    }
    if (field.relation.onUpdate) {
      parts.push(`.onUpdate('${field.relation.onUpdate}')`)
    }
  }

  return parts.join('')
}

/**
 * Get all field names that should exist in the database.
 */
export function getExpectedColumnNames(entity: { fields: Record<string, FieldDefinition> }): Set<string> {
  return new Set(Object.keys(entity.fields).filter((name) => entity.fields[name]?.db && !entity.fields[name]?.db?.virtual))
}

/**
 * Check if field is nullable.
 */
export function isFieldNullable(field: FieldDefinition): boolean {
  return field.db?.nullable !== false
}
